import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Camera, Loader2, RotateCcw, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { useGet } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";

const prep = async (f) => {
  if (!f.type.startsWith("image/") || (f.type === "image/jpeg" && f.size < 1.5e6)) return f;
  try { return await compressImage(f, 2400, 0.85); } catch (_) { return f; }
};

export function InvoiceCapture({ highlight }) {
  const nav = useNavigate();
  const lim = useGet("/inv/invoices/limits").data;
  const [kind, setKind] = useState("invoice");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const maxB = (lim?.max_mb || 20) * 1024 * 1024;
  const set = (i, patch) => setFiles((fs) => fs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const uploadOne = async (f, i) => {
    set(i, { status: "uploading", progress: 0, error: null });
    try {
      const p = await prep(f.file);
      if (p.size > maxB) throw new Error(`Larger than ${lim?.max_mb || 20} MB even after compression. Split the PDF or retake the photo.`);
      const fd = new FormData(); fd.append("file", p);
      const { data } = await api.post("/files?purpose=invoice", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000, onUploadProgress: (e) => set(i, { progress: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }) });
      set(i, { status: "done", id: data.id, progress: 100 });
      return data.id;
    } catch (e) { set(i, { status: "error", error: e.response ? errMsg(e) : e.message || "Upload interrupted — check your connection and retry." }); return null; }
  };
  const run = async (list) => {
    setBusy(true);
    const ids = [];
    for (let i = 0; i < list.length; i++) ids.push(list[i].status === "done" ? list[i].id : await uploadOne(list[i], i));
    if (ids.every(Boolean)) {
      try { const { data: inv } = await api.post("/inv/invoices", { doc_ids: ids, kind }); setFiles([]); nav(`/app/inventory/invoices/${inv.id}`); }
      catch (e) { toast.error(errMsg(e)); }
    } else toast.error("Some pages didn't upload. Fix or remove them, then tap Retry.");
    setBusy(false);
  };
  const pick = (e) => {
    const list = [...(e.target.files || [])].map((file) => ({ file, status: "queued", progress: 0 }));
    e.target.value = "";
    if (!list.length) return;
    const all = [...files.filter((f) => f.status !== "queued"), ...list];
    setFiles(all); run(all);
  };
  const manual = async () => { try { const { data: inv } = await api.post("/inv/invoices", { kind }); nav(`/app/inventory/invoices/${inv.id}`); } catch (er) { toast.error(errMsg(er)); } };
  const failed = files.some((f) => f.status === "error");
  return (
    <div className={`card-surface p-5 space-y-3 h-fit ${highlight ? "ring-2 ring-stone-900" : ""}`} data-testid="invoice-capture">
      <div className="font-display text-xl">Capture invoice</div>
      <div className="flex gap-2">{[["invoice", "Invoice"], ["credit", "Credit memo"]].map(([k, l]) => <button key={k} onClick={() => setKind(k)} className={`flex-1 h-10 rounded-lg border text-sm transition-colors ${kind === k ? "bg-stone-900 text-white" : "bg-white hover:bg-stone-50"}`} data-testid={`inv-kind-${k}`}>{l}</button>)}</div>
      <div className="grid grid-cols-2 gap-2">
        <label className={`h-14 rounded-lg bg-stone-900 text-white flex items-center justify-center gap-2 cursor-pointer text-sm font-medium ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="invoice-camera"><Camera className="h-5 w-5" />Take photo
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} disabled={busy} /></label>
        <label className={`h-14 rounded-lg border-2 border-stone-900 flex items-center justify-center gap-2 cursor-pointer text-sm font-medium ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="invoice-upload">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}Upload files
          <input type="file" accept="image/*,application/pdf,.pdf,.heic,.heif" multiple className="hidden" onChange={pick} disabled={busy} data-testid="invoice-upload-input" /></label>
      </div>
      <p className="text-xs text-stone-500" data-testid="invoice-limits">Accepted: {(lim?.types || ["PDF", "JPG", "PNG"]).join(", ")}. Up to {lim?.max_pages || 12} pages and {lim?.max_mb || 20} MB per file. Several photos = one multi-page invoice. Large photos are compressed automatically.</p>
      {files.length > 0 && <div className="space-y-1.5" data-testid="upload-queue">{files.map((f, i) => (
        <div key={i} className="rounded-lg border p-2 text-xs" data-testid={`upload-item-${i}`}>
          <div className="flex items-center gap-2"><span className="flex-1 truncate">{f.file.name}</span>
            {f.status === "uploading" && <span className="num">{f.progress}%</span>}{f.status === "done" && <CheckCircle2 className="h-4 w-4 text-emerald-700" />}{f.status === "error" && <AlertTriangle className="h-4 w-4 text-rose-700" />}
            {!busy && f.status !== "done" && <button onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remove" data-testid={`upload-remove-${i}`}><X className="h-4 w-4" /></button>}</div>
          {f.status === "uploading" && <div className="h-1 bg-stone-100 rounded mt-1"><div className="h-1 bg-stone-900 rounded transition-[width]" style={{ width: `${f.progress}%` }} /></div>}
          {f.error && <div className="text-rose-700 mt-1">{f.error}</div>}
        </div>))}
        {failed && !busy && <Button variant="outline" className="h-10 w-full" onClick={() => run(files)} data-testid="upload-retry"><RotateCcw className="h-4 w-4 mr-1" />Retry</Button>}
      </div>}
      <Button variant="outline" className="h-11 w-full" onClick={manual} disabled={busy} data-testid="invoice-manual">Enter manually</Button>
      <p className="text-xs text-stone-500">AI reads the invoice. You verify every value. Nothing reaches inventory or reports until you approve.</p>
    </div>
  );
}
